import React, { useState, useEffect } from "react";
import {
  Users,
  MapPin,
  Calendar,
  AlertTriangle,
  Waves,
  Fish,
  TrendingDown,
  Target,
  Zap,
  Heart,
  Eye,
  Activity,
  Award,
  Globe,
  Clock,
  Baby,
  Ruler,
  Weight,
  Thermometer,
  Droplets,
  Search,
  Camera,
  Brain,
  Ear,
  Shield,
  Skull,
  TreePine,
  Mountain,
  Sun,
  Moon,
  Wind,
  Navigation,
  Microscope,
  FlaskConical,
  BookOpen,
  History,
  Timer,
  Crosshair,
  Anchor,
  Radio,
} from "lucide-react";

const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return isVisible ? (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg shadow-lg transition-all duration-300 z-50"
      aria-label="Back to top"
    >
      ↑
    </button>
  ) : null;
};

const colorBg = {
  blue: "bg-blue-600",
  green: "bg-green-600",
  purple: "bg-purple-600",
  cyan: "bg-cyan-600",
  orange: "bg-orange-600",
  pink: "bg-pink-600",
  rose: "bg-rose-600",
  red: "bg-red-600",
  indigo: "bg-indigo-600",
};

const colorText = {
  blue: "text-blue-400",
  green: "text-green-400", 
  purple: "text-purple-400",
  cyan: "text-cyan-400",
  orange: "text-orange-400",
  pink: "text-pink-400",
  rose: "text-rose-400",
  red: "text-red-400",
  indigo: "text-indigo-400",
};

const About = () => {
  const [hoveredFact, setHoveredFact] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [populationCounter, setPopulationCounter] = useState(600);

  useEffect(() => {
    const interval = setInterval(() => {
      setPopulationCounter((prev) => (prev > 10 ? Math.max(prev - Math.random() * 5, 10) : prev));
    }, 150);
    const timeout = setTimeout(() => clearInterval(interval), 3000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const galleryPhotos = [
    {
      src: "https://upload.wikimedia.org/wikipedia/commons/9/94/Vaquita3_Olson_NOAA.jpg",
      title: "Single Vaquita",
      subtitle: "Solitary sighting",
      description: "A lone vaquita surfacing, one of the rare photographs available",
      citation: "Paula Olson, NOAA, Public Domain",
    },
    {
      src: "https://upload.wikimedia.org/wikipedia/commons/3/38/Vaquita6_Olson_NOAA.jpg",
      title: "Mother and Calf",
      subtitle: "Extremely rare",
      description: "Extremely rare photograph of a vaquita mother with her calf",
    },
    {
      src: "https://live.staticflickr.com/6151/6167212322_d2728b372c.jpg",
      title: "Five vaquitas all togther!",
      subtitle: "Extremely rare",
      description: "Seashepard spotted five vaquitas together in one of the few group sightings ever documented.",
    },
        {
      src: "https://upload.wikimedia.org/wikipedia/commons/7/77/Vaquita1_Olson_NOAA.jpg",
      title: "Two vaquitas from 2008",
      subtitle: "Extremely rare",
      description: "A rare photograph of two vaquitas surfacing together, taken during a 2008 research expedition.",
      citation: "Paula Olson, NOAA, Public Domain",
      citationLink: "https://commons.wikimedia.org/wiki/File:Vaquita6_Olson_NOAA.jpg",
      wikimediaText: ", via Wikimedia Commons",
    },
     {
      src: "https://upload.wikimedia.org/wikipedia/commons/b/b4/Vaquita5_Olson_NOAA.jpg",
      title: "Another one from 2008",
      subtitle: "Extremely rare",
      description: "Another rare photograph of a vaquita taken during the same 2008 research expedition.",
    },
  ];

  useEffect(() => {
    const slideTimer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryPhotos.length);
    }, 4000);
    return () => clearInterval(slideTimer);
  }, [galleryPhotos.length]);

  const animalFacts = [
    {
      icon: Fish,
      label: "Species Type",
      value: "Porpoise (Cetacean)",
      color: "blue",
      detail:
        "Vaquitas belong to Phocoenidae. Unlike dolphins, porpoises have spade-shaped teeth and are smaller and stockier.",
    },
    {
      icon: Ruler,
      label: "Size",
      value: "1.2–1.5 m",
      color: "green",
      detail:
        "Females up to 1.5 m, males to ~1.4 m. Smallest cetacean. Reverse sexual dimorphism: females larger.",
    },
    {
      icon: Weight,
      label: "Weight",
      value: "27–68 kg",
      color: "purple",
      detail:
        "Compact build suits shallow, turbulent waters. Females heavier on average.",
    },
    {
      icon: Eye,
      label: "Distinctive Features",
      value: "Dark eye and lip patches",
      color: "cyan",
      detail:
        "Dark facial patches and rounded head with no beak make vaquitas identifiable.",
    },
    {
      icon: MapPin,
      label: "Habitat",
      value: "Only Gulf of California",
      color: "orange",
      detail:
        "Shallow, turbid waters in the northern Gulf. Range ~4,000 km² with depths <150 m.",
    },
    {
      icon: Clock,
      label: "Lifespan",
      value: "~20–21 years",
      color: "pink",
      detail:
        "Similar to harbor porpoise. Generation time ~10 years.",
    },
    {
      icon: Baby,
      label: "Sexual Maturity",
      value: "3–6 years",
      color: "rose",
      detail:
        "Likely ~3 years. Reproduction about every 2 years.",
    },
    {
      icon: Thermometer,
      label: "Body Adaptation",
      value: "Heat-adapted build",
      color: "red",
      detail:
        "Relatively slender vs cold-water relatives to aid heat dissipation.",
    },
    {
      icon: Microscope,
      label: "Teeth",
      value: "34–40 spade-shaped",
      color: "indigo",
      detail:
        "Unicuspid spade/acorn teeth typical of porpoises. Dolphins have conical teeth.",
    },
  ];

  const behaviorFacts = [
    {
      icon: Wind,
      title: "Breathing & Surfacing",
      description:
        "Surface quickly and quietly, dorsal fin/back visible <2 s.",
      detail:
        "Shy behavior makes observation difficult. Avoid boats and rarely splash or leap.",
    },
    {
      icon: Waves,
      title: "Swimming & Movement",
      description:
        "Slow, deliberate swimmers in shallow murky waters <50 m.",
      detail:
        "Non-migratory. Stay within small range year-round.",
    },
    {
      icon: Users,
      title: "Social Structure",
      description:
        "Alone or in small groups of 2–3, often mother-calf pairs.",
      detail:
        "Rare loose groups up to ~8–10 individuals.",
    },
    {
      icon: Target,
      title: "Foraging & Echolocation",
      description:
        "Demersal fish, squid, crustaceans; high-frequency clicks to locate prey.",
      detail:
        "Diet includes endemic Gulf species. Hunt mainly at dawn/dusk.",
    },
    {
      icon: Sun,
      title: "Daily Activity",
      description:
        "Diurnal with feeding peaks at dawn and dusk.",
      detail:
        "Rhythm tied to tides and prey availability.",
    },
    {
      icon: Shield,
      title: "Predator Avoidance",
      description:
        "Elusive behavior reduces predation by sharks and killer whales.",
      detail:
        "Stealth and small groups limit detection.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="pt-24">

        {/* Main container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 bg-slate-800 px-6 py-3 rounded-lg border border-slate-700 mb-8">
              <Fish className="h-5 w-5 text-blue-400" />
              <span className="text-white font-medium">About the Species</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Meet the <span className="block text-purple-400">Vaquita</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed mb-8">
              The world's smallest and most endangered cetacean. A shy porpoise found only in Mexico's Gulf of California.
            </p>
          </div>

          {/* 1. BIOLOGICAL PROFILE */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Biological Profile</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-6">What is a Vaquita?</h3>
                  <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                    <p>
                      The vaquita (Phocoena sinus) is a small porpoise. The name means "little cow" in Spanish. They were also called the "panda of the sea" due to the distinct black markings around their eyes and lips.
                    </p>
                    <p>
                      Described in 1958 from skulls found on beaches, it is the smallest and rarest porpoise in the world.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800 border border-slate-700 p-6 rounded-lg">
                  <h3 className="text-2xl font-semibold text-blue-400 mb-4">Key Distinguishing Features</h3>
                  <ul className="space-y-3">
                    {[
                      "Dark rings around eyes and lips",
                      "Tall triangular dorsal fin",
                      "Stocky body for shallow water",
                      "Gray back with pale underside",
                      "No beak; blunted rostrum",
                      "34–40 spade-shaped teeth",
                    ].map((t) => (
                      <li key={t} className="flex items-start space-x-3">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mt-3" />
                        <span className="text-slate-300">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/1/18/Vaquita_swimming_through_the_void._%28Phocoena_Sinus%29_2.png"
                    alt="Vaquita research documentation"
                    className="w-full h-80 object-cover rounded-lg mb-4"
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.src =
                        "https://via.placeholder.com/800x400/334155/94a3b8?text=Vaquita+Research+Photo";
                    }}
                  />
                  <p className="text-sm text-slate-400">
                    <a
                      href="https://creativecommons.org/licenses/by-sa/4.0"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-400 transition-colors"
                    >
                      좀비 브렌다, CC BY-SA 4.0
                    </a>
                    , via Wikimedia Commons
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 2. ANIMAL FACTS (Physical Characteristics) */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Physical Characteristics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {animalFacts.map((fact, index) => {
                const Bg = colorBg[fact.color] ?? "bg-slate-600";
                const Tx = colorText[fact.color] ?? "text-slate-300";
                const Icon = fact.icon;
                return (
                  <div
                    key={fact.label}
                    className="bg-slate-800 border border-slate-700 p-8 rounded-lg hover:border-slate-600 transition-all duration-300 cursor-pointer group"
                    onMouseEnter={() => setHoveredFact(index)}
                    onMouseLeave={() => setHoveredFact(null)}
                  >
                    <div className="flex items-center mb-4">
                      <div className={`w-12 h-12 ${Bg} rounded-lg flex items-center justify-center mr-4`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{fact.label}</h3>
                    </div>
                    <p className={`text-2xl font-bold ${Tx} mb-3`}>{fact.value}</p>
                    {hoveredFact === index && (
                      <div className="mt-4 p-4 bg-slate-700 rounded-lg border border-slate-600">
                        <p className="text-slate-300 text-sm leading-relaxed">{fact.detail}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. RARE DOCUMENTATION */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Rare Documentation</h2>
            <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
              <div className="text-center mb-8">
                <Camera className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                <p className="text-slate-300 leading-relaxed max-w-3xl mx-auto">
                  Unfortunately, these are the only photos I can find that are under free liscense. Vaquitas are extremely elusive and shy, making them very difficult to photograph in the wild.
                </p>
              </div>

              <div className="relative">
                <div className="overflow-hidden rounded-lg">
                  <img
                    src={galleryPhotos[currentImageIndex].src}
                    alt={galleryPhotos[currentImageIndex].title}
                    className="w-full h-96 object-cover"
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.src =
                        "https://via.placeholder.com/800x400/1e293b/94a3b8?text=Image+Not+Available";
                    }}
                  />
                </div>

                <div className="mt-6 text-center">
                  <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                    <h4 className="text-xl font-bold text-white mb-2">
                      {galleryPhotos[currentImageIndex].title}
                    </h4>
                    {galleryPhotos[currentImageIndex].subtitle && (
                      <p className="text-purple-400 font-semibold mb-2">
                        {galleryPhotos[currentImageIndex].subtitle}
                      </p>
                    )}
                    <p className="text-slate-300 mb-4">
                      {galleryPhotos[currentImageIndex].description}
                    </p>
                    <p className="text-slate-400 text-sm">
                      <a
                        href={galleryPhotos[currentImageIndex].citationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-400 transition-colors"
                      >
                        {galleryPhotos[currentImageIndex].citation}
                      </a>
                      {galleryPhotos[currentImageIndex].wikimediaText}
                    </p>
                  </div>
                </div>

                <div className="flex justify-center mt-6 space-x-2">
                  {galleryPhotos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex
                          ? "bg-purple-400"
                          : "bg-slate-600 hover:bg-slate-500"
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 4. BEHAVIOR & LIFESTYLE */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Behavior & Lifestyle</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {behaviorFacts.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                    <div className="flex items-center mb-4">
                      <Icon className="h-8 w-8 text-blue-400 mr-4" />
                      <h3 className="text-xl font-semibold text-white">{b.title}</h3>
                    </div>
                    <p className="text-slate-300 mb-4 leading-relaxed">{b.description}</p>
                    <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm leading-relaxed">{b.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 5. REPRODUCTION & LIFE CYCLE */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Reproduction & Life Cycle</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <Heart className="h-8 w-8 text-pink-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Mating System</h3>
                </div>
                <div className="space-y-6">
                  <div className="bg-pink-900/20 border border-pink-600/30 p-4 rounded-lg">
                    <h4 className="text-pink-400 font-semibold mb-2">Polygynous Competition</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Evidence of sperm competition: large testes (~3% body mass), females larger, small groups.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-700 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-400 mb-1">April–May</div>
                      <div className="text-slate-300 text-sm">Mating Season</div>
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-400 mb-1">~2 years</div>
                      <div className="text-slate-300 text-sm">Breeding Interval</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <Baby className="h-8 w-8 text-green-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Life Cycle</h3>
                </div>
                <div className="relative space-y-6">
                  <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-green-400/30" />
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Timer className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400">Gestation: 10.5–11 months</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Mating in April–May followed by ~11 months pregnancy.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Baby className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-400">Birth: February–April</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Single calves ~68 cm at birth.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Droplets className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-purple-400">Nursing: 6–8 months</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Weaning occurs before independence.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 6. DIET & ECOSYSTEM ROLE */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Diet & Ecosystem Role</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <Fish className="h-8 w-8 text-cyan-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Feeding Habits</h3>
                </div>
                <div className="space-y-6">
                  <p className="text-slate-300 leading-relaxed">
                    Generalist feeder in shallow-water habitats, meaning that they feed on a variety of fish and invertebrates other than specializing in just one or a few prey species. Studies show that fish make up the majority of their diet.
                  </p>
                  <div className="space-y-4">
                    <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg">
                      <h4 className="text-cyan-400 font-semibold mb-2">Primary Prey Species</h4>
                      <ul className="space-y-2 text-sm">
                        {[
                          "Silver weakfish (Isopisthus altipinnis)",
                          "Mimetic midshipman (Porichthys mimeticus)",
                          "Bronze-striped grunt (Orthopristis reddingi)",
                          "Ronco croaker (Bairdiella icistia)",
                          "Small squids and crustaceans",
                        ].map((s) => (
                          <li key={s} className="flex items-start space-x-2">
                            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2" />
                            <span className="text-slate-300">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <Radio className="h-8 w-8 text-purple-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Echolocation & Hunting</h3>
                </div>
                <div className="space-y-6">
                  <p className="text-slate-300 leading-relaxed">
                    Vaquitas use high-frequency echolocation clicks, forming sound picture to navigate and locate prey, especially in turbid, sediment-rich habitat. 
                  </p>
                  <div className="space-y-4">
                    <div className="bg-purple-900/20 border border-purple-600/30 p-4 rounded-lg">
                      <h4 className="text-purple-400 font-semibold mb-2">Hunting Technique</h4>
                      <ul className="space-y-3 text-sm">
                        {[
                          "Clicks locate prey in or near sediments",
                          "Forages on benthic and demersal species",
                          "Peak hunting at dawn and dusk",
                          "Suction feeding; swallows prey whole",
                        ].map((s) => (
                          <li key={s} className="flex items-start space-x-2">
                            <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2" />
                            <span className="text-slate-300">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-cyan-400 mb-1">&lt;50m</div>
                        <div className="text-slate-300 text-sm">Foraging Depth</div>
                      </div>
                      <div className="bg-slate-700 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-400 mb-1">19</div>
                        <div className="text-slate-300 text-sm">Prey Species ID'd</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 7. GENETIC HERITAGE & CLASSIFICATION */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Genetic Heritage & Classification
            </h2>

            {/* Genetic Heritage */}
            <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg mb-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="flex items-center mb-4">
                    <Brain className="h-8 w-8 text-orange-400 mr-4" />
                    <h3 className="text-2xl font-semibold text-white">Historical Population Size</h3>
                  </div>
                  <p className="text-slate-300 leading-relaxed">
                    Genetic analysis of vaquita genomes suggests that their population was naturally low and stable ranging from 2000 to 5000 individuals. The bottleneck, meaning a sharp reduction in population size, likely occurred over evolutionary time rather than recently.
                  </p>
                  <div className="bg-orange-900/20 border border-orange-600/30 p-4 rounded-lg">
                    <h4 className="text-orange-400 font-semibold mb-2">Ancient Bottleneck</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Low diversity accrued over evolutionary time. Limited inbreeding depression expected.
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center mb-4">
                    <FlaskConical className="h-8 w-8 text-blue-400 mr-4" />
                    <h3 className="text-2xl font-semibold text-white">Genetic Findings</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg">
                      <h4 className="text-blue-400 font-semibold mb-2">DNA Analysis Results</h4>
                      <ul className="space-y-2 text-sm">
                        {[
                          "No variation in mitochondrial DNA control region",
                          "Low diversity in immune system genes (MHC)",
                          "Genetic variability loss over evolutionary history",
                          "Recent studies suggest inbreeding depression risk is low",
                        ].map((s) => (
                          <li key={s} className="flex items-start space-x-2">
                            <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2" />
                            <span className="text-slate-300">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-green-900/20 border border-green-600/30 p-4 rounded-lg">
                      <h4 className="text-green-400 font-semibold mb-2">Hope for Recovery</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Recent research suggests that the risk of inbreeding depression in vaquita is low, since weak or harmful genes have mostly been filtered out by natural selection over time. This means that if threats are mitigated, the population has an actual hope for recovery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Classification & Taxonomy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <FlaskConical className="h-8 w-8 text-blue-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Scientific Classification</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {[
                      ["Kingdom", "Animalia"],
                      ["Phylum", "Chordata"],
                      ["Class", "Mammalia"],
                      ["Order", "Cetartiodactyla"],
                      ["Family", "Phocoenidae"],
                      ["Genus", "Phocoena"],
                    ].map(([k, v]) => (
                      <div key={k} className="bg-slate-700 p-3 rounded">
                        <span className="text-slate-400">{k}:</span>
                        <div className="text-white font-semibold">{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-purple-900/20 border border-purple-600/30 p-4 rounded-lg">
                    <h4 className="text-purple-400 font-semibold mb-2">Closest Relatives</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-300">Burmeister's porpoise</span>
                        <span className="text-slate-400">(P. spinipinnis)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Spectacled porpoise</span>
                        <span className="text-slate-400">(A. dioptrica)</span>
                      </div>
                      <p className="text-slate-400 text-xs mt-3">
                        Southern Hemisphere distribution supports northward migration origin.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="flex items-center mb-6">
                  <History className="h-8 w-8 text-purple-400 mr-4" />
                  <h3 className="text-2xl font-semibold text-white">Evolutionary History</h3>
                </div>
                <div className="space-y-6">
                  <div className="bg-yellow-900/20 border border-yellow-600/30 p-4 rounded-lg">
                    <h4 className="text-yellow-400 font-semibold mb-2">K-Selected Species</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      K-selected species mean that they invest more in fewer offspring. Examples of traits include longer lifespans, later maturity, and extended parental care. You can often see photos of mother vaquitas with their calves for this reason. Slow reproduction and few offspring make recovery hard after declines.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Population Crisis */}
          <div className="mb-20">
            <div className="bg-red-900/20 border border-red-600/30 p-12 rounded-lg text-center">
              <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-white mb-6">The Conservation Crisis</h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-4xl mx-auto">
                From ~567 in 1997 to fewer than {Math.floor(populationCounter)} today. {">"}98% decline.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
                {[
                  ["1997", "567 individuals", "text-blue-400"],
                  ["2007", "~150 individuals", "text-yellow-400"],
                  ["2018", "~19 individuals", "text-orange-400"],
                  ["2025", "<10 individuals", "text-red-400"],
                ].map(([year, label, color]) => (
                  <div key={year} className="bg-slate-800 border border-slate-700 p-6 rounded-lg">
                    <div className={`text-3xl font-bold ${color} mb-2`}>{year}</div>
                    <div className="text-slate-300">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-slate-800 border border-slate-700 p-12 rounded-lg">
              <h3 className="text-3xl font-bold text-white mb-6">Learn More About the Crisis</h3>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                Understand the threats and how to help save this species from extinction.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-lg transition-all duration-300">
                  Threats &amp; Challenges
                </button>
                <button className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-4 rounded-lg transition-all duration-300">
                  Take Action Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BackToTop />
    </div>
  );
};

export default About;